import { Button, Form, Input, Radio } from "antd"
import { t } from "i18next"
import React, { useEffect, useState } from "react"
import ReactQuill from "react-quill"
import { configs } from "~/configs"
import { BaseData, BaseFormProps, FieldType, MediaData } from "~/types/types"
import baseService from "../service/base"

const { TextArea } = Input

const useBlockContent = (base: BaseData | null) => {
  const [isUseBlockContent, setIsUseBlockContent] = useState(false)
  const [blockContentId, setBlockContentId] = useState<any>(null)

  React.useEffect(() => {
    if (base?.attributes?.blockContent?.data || base?.attributes?.isUseUIBuilder) {
      setIsUseBlockContent(true)
      setBlockContentId(base.attributes.blockContent.data?.id)
    }
  }, [base])

  return { isUseBlockContent, blockContentId }
}

const BaseForm: React.FC<BaseFormProps> = ({
  base,
  edit,
  form,
  onFinishFailed,
  quillValue,
  setQuillValue,
  onFinish,
  onChangeMetaImage,
  isUIBuilderPage,
}) => {
  const quillRef = React.useRef<ReactQuill>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  // const [selectedMetaImage, setSelectedMetaImage] = useState<string | null>(null)
  const [modalMetaImage, setModalMetaImage] = useState(false)
  const baseServices = baseService()

  const handleSubmit = () => {
    const editorContent = quillRef.current?.getEditor().root.innerHTML || ""
    form.setFieldsValue({ content: editorContent })
    onFinish(form.getFieldsValue())
  }

  useEffect(() => {
    if (quillValue && quillRef.current) {
      const editor = quillRef.current.getEditor()
      editor.root.innerHTML = quillValue
    }
  }, [quillValue])

  // const handleOpenModal = (modalMetaImage: boolean) => {
  //   setModalMetaImage(modalMetaImage)
  //   setIsModalOpen(true)
  // }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const handleSelectMedia = (media: MediaData) => {
    const quillEditor = quillRef.current?.getEditor()

    if (quillEditor) {
      const range = quillEditor.getSelection(true)
      quillEditor.insertEmbed(range.index, "image", `${configs.API_URL}${media.url}`)
    }

    setIsModalOpen(false)
  }

  const handleNameBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const nameValue = e.target.value.trim()
    if (!nameValue) {
      form.setFields([{ name: "slug", errors: [] }])
      return
    }

    const slugValue = nameValue

    const isSlugUnique = async (slug: string): Promise<boolean> => {
      try {
        const response = await baseServices.checkSlug(slug)

        return response.data.length === 0
      } catch {
        return false
      }
    }

    const isUnique = await isSlugUnique(slugValue)
    if (!isUnique) {
      form.setFieldsValue({ slug: slugValue })
      form.setFields([{ name: "slug", errors: [t("Slug already exists. Please choose another slug.")] }])
    } else {
      form.setFieldsValue({ slug: slugValue })
      form.setFields([{ name: "slug", errors: [] }])
    }
  }

  const isValidJson = (str: string): boolean => {
    try {
      JSON.parse(str)
    } catch (e) {
      return false
    }
    return true
  }

  // const handleSelectMetaImage = (media: MediaData) => {
  //   setSelectedMetaImage(`${configs.API_URL}${media.url}`)
  //   onChangeMetaImage(media)
  //   setIsModalOpen(false)
  // }

  const { isUseBlockContent, blockContentId } = useBlockContent(base)

  return (
    <Form
      form={form}
      layout="vertical"
      name="basic"
      initialValues={base}
      onFinish={handleSubmit}
      onFinishFailed={onFinishFailed}
      className="flex flex-col w-[70%]"
      autoComplete="off"
    >
      <Form.Item
        label={t("Facility name")}
        name="name"
        rules={[{ required: true, message: t("Please enter the name of the facility!") }]}
      >
        <Input placeholder={t("Enter new facility name")} />
      </Form.Item>
      <Form.Item
        label={t("Email")}
        name="email"
        rules={[
          { required: true, message: t("Please enter a valid email address") },
          { type: "email", message: t("Invalid email format") },
        ]}
      >
        <Input placeholder={t("Enter email")} />
      </Form.Item>
      <Form.Item
        label={t("Hotline")}
        name="hotline"
        rules={[{ required: true, message: t("Please enter the hotline!") }]}
      >
        <Input placeholder={t("Enter hotline number")} />
      </Form.Item>
      <Form.Item
        label={t("Working time")}
        name="workingTime"
        rules={[{ required: true, message: t("Please enter working time!") }]}
      >
        <Input placeholder={t("E.g., Mon-Sun, 8:00 AM - 9:00 PM")} />
      </Form.Item>
      <Form.Item label={t("Status")} name="status" rules={[{ required: true, message: t("Please select status!") }]}>
        <Radio.Group>
          <Radio value={true}>{t("Active")}</Radio>
          <Radio value={false}>{t("Inactive")}</Radio>
        </Radio.Group>
      </Form.Item>
      <Form.Item
        label={t("Address")}
        name="address"
        rules={[{ required: true, message: t("Please enter the address!") }]}
      >
        <TextArea rows={2} placeholder={t("Enter full address")} />
      </Form.Item>
      <Form.Item
        label={t("Latitude")}
        name="latitude"
        rules={[
          {
            required: true,
            message: t("Please enter the latitude (Google Maps)!"),
          },
        ]}
      >
        <Input placeholder={t("Enter latitude")} />
      </Form.Item>
      <Form.Item
        label={t("Longitude")}
        name="longitude"
        rules={[
          {
            required: true,
            message: t("Please enter the longitude (Google Maps)!"),
          },
        ]}
      >
        <Input placeholder={t("Enter longitude")} />
      </Form.Item>
      <Form.Item
        label={t("Link Map")}
        name="linkMap"
        rules={[
          {
            required: true,
            message: t("Please enter the Google Maps link!"),
          },
        ]}
      >
        <Input placeholder={t("Enter Google Maps link")} />
      </Form.Item>
      <Form.Item
        label={t("Link Post")}
        name="linkPost"
        rules={[{ required: true, message: t("Please enter the post link!") }]}
      >
        <Input placeholder={t("Enter post link")} />
      </Form.Item>

      <span className="text-base font-medium pt-2 pb-2">{t("SEO")}</span>

      <div className="grid grid-cols-2 gap-x-5 gap-y-2 p-5 border rounded-[10px]">
        <Form.Item<FieldType>
          name="metaTitle"
          label={<span className="text-base font-medium p">{t("Meta Title")}</span>}
        >
          <Input className="custom-input py-2" />
        </Form.Item>

        <Form.Item<FieldType>
          name="keywords"
          label={<span className="text-base font-medium">{t("Meta Keywords")}</span>}
        >
          <Input className="custom-input py-2" />
        </Form.Item>

        <Form.Item<FieldType>
          name="metaDescription"
          label={<span className="text-base font-medium">{t("Meta Description")}</span>}
        >
          <Input.TextArea className="custom-input py-2" rows={5} />
        </Form.Item>

        <Form.Item<FieldType>
          name="structuredData"
          label={<span className="text-base font-medium">{t("Structured Data")}</span>}
          rules={[
            {
              validator: (_, value) => {
                if (value && !isValidJson(value)) {
                  return Promise.reject(new Error(t("Invalid JSON format!")))
                }
                return Promise.resolve()
              },
            },
          ]}
        >
          <Input.TextArea className="custom-input py-2" rows={5} placeholder={t('@example: {"id": "string"}')} />
        </Form.Item>

        <Form.Item<FieldType>
          name="metaRobots"
          label={<span className="text-base font-medium">{t("Meta Robots")}</span>}
        >
          <Input className="custom-input py-2" />
        </Form.Item>

        <Form.Item<FieldType>
          name="metaViewport"
          label={<span className="text-base font-medium">{t("Meta Viewport")}</span>}
        >
          <Input className="custom-input py-2" />
        </Form.Item>
      </div>
      <div>
        <Form.Item>
          <Button type="primary" htmlType="submit" className="mt-[10px]">
            {t("Save")}
          </Button>
        </Form.Item>
      </div>
    </Form>
  )
}

export default BaseForm
